/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { createSlice } from "@reduxjs/toolkit";
import { format } from "date-fns";

const initialState = {
  selectedLocation: null,
  selectedServices: [],
  selectedProvider: null,
  selectedCategories: [],
  selectedProducts: [],
  selectedDate: format(new Date(), "yyyy-MM-dd"),
  selectedTime: null,
  timeValidatedProducts: null,
  invoice: null,
  bookingRequest: {
    count: 1,
    service_id: null,
    provider_id: null,
    start_datetime: null,
    // "sheduler_channel" is the backend field name (API contract)
    sheduler_channel: null,
    client_membership_id: null,
  },
  schedulerChannel: null,
  promotionCode: null,
  bookingResponse: null,
  scrollToServiceId: null,
  isExclusiveAddonNoteShown: false,
  // UI state for local operations only
  isBookingLoading: false,
  bookingError: null,
  // Membership access check: the service currently being evaluated. Setting
  // this triggers MembershipServiceAccessModals to render the right modal
  // based on useServiceMembershipAccess's verdict.
  pendingAccessService: null,
  // Per-service membership grants, keyed by serviceId (value is a MembershipGrant
  // minus serviceId — see utils/membershipGrant.js). Feeds client_membership_id
  // in the payload and the applied-membership summary.
  membershipAccessGrants: {},
};

export const buildProductsForRequest = (selectedServices) => {
  const products = selectedServices.map(service =>
    [ ...(service.addons ?? []), ...(service.products ?? [])].map(({ id, qty }) => ({
      product_id: id,
      qty,
    }))
  );
  if (!products.some(p => p.length > 0)) return null;
  if (products.length === 1) return products[0];
  return products;
};

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    setSelectedLocation: (state, action) => {
      state.selectedLocation = action.payload;
      autoUpdateBookingRequest(state);
    },

    setSelectedServices: (state, action) => {
      state.selectedServices = action.payload;
      autoUpdateBookingRequest(state);
    },

    addSelectedService: (state, action) => {
      const service = action.payload;
      const existingIndex = state.selectedServices.findIndex((s) => s.id === service.id);

      if (existingIndex === -1) {
        state.selectedServices.push({ ...service, addons: [], isAddonsInitialized: false, products: [], isProductsInitialized: false });
        autoUpdateBookingRequest(state);
      }
    },

    addDefaultAddons: (state, action) => {
      const { serviceId, addons } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      if (!service.addons || service.addons.length === 0) {
        service.addons = Array.isArray(addons) ? addons : [addons];
        service.isAddonsInitialized = true;
        autoUpdateBookingRequest(state);
      }
    },

    toggleAddon: (state, action) => {
      const { serviceId, addon, exclusive = false } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      const index = service.addons?.findIndex(a => +a.id === +addon.id);

      if (index > -1) {
        service.addons = service.addons.filter(a => +a.id !== +addon.id);
      } else {
        service.addons = [
          ...(exclusive ? [] : (service.addons ?? [])),
          { ...addon, qty: 1 }
        ];
      }
      autoUpdateBookingRequest(state);
    },

    markExclusiveAddonNoteShown: (state) => {
      state.isExclusiveAddonNoteShown = true;
    },

    updateAddonQty: (state, action) => {
      const { serviceId, addon, qty } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      const index = service.addons?.findIndex(a => +a.id === +addon.id);
      if (index > -1) {
        service.addons[index].qty = qty;
        autoUpdateBookingRequest(state);
      }
    },

    addDefaultProducts: (state, action) => {
      const { serviceId, products } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      if (!service.products || service.products.length === 0) {
        service.products = Array.isArray(products) ? products : [products];
        service.isProductsInitialized = true;
        autoUpdateBookingRequest(state);
      }
    },

    toggleProduct: (state, action) => {
      const { serviceId, product } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      const index = service.products?.findIndex(p => +p.id === +product.id);

      if (index > -1) {
        service.products = service.products.filter(p => +p.id !== +product.id);
      } else {
        service.products = [
          ...(service.products ?? []),
          { ...product, qty: 1 }
        ];
      }
      autoUpdateBookingRequest(state);
    },

    updateProductQty: (state, action) => {
      const { serviceId, product, qty } = action.payload;
      const service = state.selectedServices.find(s => +s.id === +serviceId);
      if (!service) return;

      const index = service.products?.findIndex(p => +p.id === +product.id);
      if (index > -1) {
        service.products[index].qty = qty;
        autoUpdateBookingRequest(state);
      }
    },

    removeSelectedService: (state, action) => {
      const serviceId = action.payload;
      state.selectedServices = state.selectedServices
        .filter((s) => s.id !== serviceId)
        .map((s) => ({ ...s, addons: [], isAddonsInitialized: false, products: [], isProductsInitialized: false }));
      autoUpdateBookingRequest(state);
    },

    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
      autoUpdateBookingRequest(state);
    },

    setSelectedCategories: (state, action) => {
      state.selectedCategories = action.payload;
      autoUpdateBookingRequest(state);
    },

    addSelectedCategory: (state, action) => {
      const category = action.payload;
      const existingIndex = state.selectedCategories.findIndex((c) => c.id === category.id);
      if (existingIndex === -1) {
        state.selectedCategories.push(category);
        autoUpdateBookingRequest(state);
      }
    },

    removeSelectedCategory: (state, action) => {
      const categoryId = action.payload;
      state.selectedCategories = state.selectedCategories.filter((c) => c.id !== categoryId);
      autoUpdateBookingRequest(state);
    },

    setSelectedProducts: (state, action) => {
      state.selectedProducts = action.payload;
      autoUpdateBookingRequest(state);
    },

    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
      autoUpdateBookingRequest(state);
    },

    setSelectedTime: (state, action) => {
      state.selectedTime = action.payload;
      state.timeValidatedProducts = action.payload ? buildProductsForRequest(state.selectedServices) : null;
      autoUpdateBookingRequest(state);
    },

    resetSelectedDate: (state) => {
      state.selectedDate = format(new Date(), "yyyy-MM-dd");
      autoUpdateBookingRequest(state);
    },

    updateBooking: (state, action) => {
      const updatedData = action.payload;
      state.bookingRequest = { ...state.bookingRequest, ...updatedData };
    },

    setBookingResponse: (state, action) => {
      state.bookingResponse = action.payload;
    },

    setInvoice: (state, action) => {
      state.invoice = action.payload;
    },

    resetBookingRequest: (state) => {
      state.selectedServices = [];
      state.selectedProvider = null;
      state.selectedCategories = [];
      state.selectedProducts = [];
      state.selectedDate = format(new Date(), "yyyy-MM-dd");
      state.selectedTime = null;
      state.membershipAccessGrants = {};
      state.scrollToServiceId = null;
      state.bookingRequest = {
        count: 1,
        service_id: null,
        provider_id: null,
        start_datetime: null,
      };
    },

    resetBookingState: (state) => {
      return { ...initialState };
    },

    setSchedulerChannel: (state, action) => {
      state.schedulerChannel = action.payload;
      autoUpdateBookingRequest(state);
    },

    resetSchedulerChannel: (state) => {
      state.schedulerChannel = null;
      autoUpdateBookingRequest(state);
    },

    setScrollToServiceId: (state, action) => {
      state.scrollToServiceId = action.payload ?? null;
    },

    clearScrollToServiceId: (state) => {
      state.scrollToServiceId = null;
    },

    setPromotionCode: (state, action) => {
      state.promotionCode = action.payload;
    },

    clearPromotionCode: (state) => {
      state.promotionCode = null;
    },

    // UI state actions (for local state management only)
    setBookingLoading: (state, action) => {
      state.isBookingLoading = action.payload;
    },

    setBookingError: (state, action) => {
      state.bookingError = action.payload;
      state.isBookingLoading = false;
    },

    clearBookingError: (state) => {
      state.bookingError = null;
    },

    setPendingAccessService: (state, action) => {
      state.pendingAccessService = action.payload || null;
    },

    clearPendingAccessService: (state) => {
      state.pendingAccessService = null;
    },

    // Merge one service's grant into the map, keeping other services' grants.
    // Payload is a MembershipGrant (utils/membershipGrant.js); serviceId is the
    // map key, the rest is the stored value.
    setMembershipAccessGrant: (state, action) => {
      const { serviceId, ...grant } = action.payload || {};
      if (serviceId == null) return;

      state.membershipAccessGrants[serviceId] = grant;
      autoUpdateBookingRequest(state);
    },

    // Drop all grants on logout / session expiry; selected services stay.
    clearMembershipAccessGrants: (state) => {
      state.membershipAccessGrants = {};
      autoUpdateBookingRequest(state);
    },
  },
});

function autoUpdateBookingRequest(state) {
  const {
    selectedServices,
    selectedProvider,
    selectedCategories,
    selectedProducts,
    selectedDate,
    selectedTime,
    selectedLocation,
    schedulerChannel,
  } = state;

  const extractIds = (items) => (items ? (items.length === 1 ? items[0].id : items.map((item) => item.id)) : null);
  const formatDateTime = (date, time) => (date && time ? `${date} ${time}` : null);
  const getBookingCount = (services) =>
    services.find((service) => service.group_booking_count)?.group_booking_count || 1;
  // Aligned to service_id: scalar for one service, array for several, null when
  // none use a membership. A grant counts only while its service is selected.
  const getClientMembershipIds = (services) => {
    const grants = state.membershipAccessGrants || {};
    const ids = services.map((service) => {
      const grant = grants[service.id];
      return grant && grant.clientMembershipId != null ? grant.clientMembershipId : null;
    });
    if (!ids.some((id) => id != null)) return null;
    return ids.length === 1 ? ids[0] : ids;
  };

  const prepareProductsForRequest = (selectedServices) => {
    return buildProductsForRequest(selectedServices);
  };

  const coreBookingData = {
    service_id: extractIds(selectedServices),
    provider_id: selectedProvider?.id || null,
    category_id: extractIds(selectedCategories),
    start_datetime: formatDateTime(selectedDate, selectedTime),
    location_id: selectedLocation?.id || null,
    count: getBookingCount(selectedServices),
    // "sheduler_channel" is the backend field name (API contract)
    sheduler_channel: schedulerChannel || null,
    products: prepareProductsForRequest(selectedServices),
    client_membership_id: getClientMembershipIds(selectedServices),
  };

  state.bookingRequest = { ...state.bookingRequest, ...coreBookingData };
}

export const {
  setSelectedLocation,
  setSelectedServices,
  addSelectedService,
  removeSelectedService,
  setSelectedProvider,
  setSelectedCategories,
  addSelectedCategory,
  removeSelectedCategory,
  setSelectedProducts,
  setSelectedDate,
  setSelectedTime,
  resetSelectedDate,
  updateBooking,
  setBookingResponse,
  setInvoice,
  resetBookingRequest,
  resetBookingState,
  setBookingLoading,
  setBookingError,
  clearBookingError,
  setSchedulerChannel,
  resetSchedulerChannel,
  addDefaultAddons,
  toggleAddon,
  markExclusiveAddonNoteShown,
  updateAddonQty,
  addDefaultProducts,
  toggleProduct,
  updateProductQty,
  setPromotionCode,
  clearPromotionCode,
  setScrollToServiceId,
  clearScrollToServiceId,
  setPendingAccessService,
  clearPendingAccessService,
  setMembershipAccessGrant,
  clearMembershipAccessGrants,
} = bookingSlice.actions;

export default bookingSlice.reducer;
